/**
 * WhereToBuySection - Material Detail Component
 * =============================================
 * Shows professionals who have declared availability
 * for a specific material. Includes contact forms and
 * tracking for lead generation.
 * 
 * @version 1.1
 * All content CMS-driven from blueprint_professionals_config
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, Phone, MessageSquare, Star, Check, Loader2, 
  ChevronRight, Building2, ExternalLink, X
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import supabase from '../lib/supabase';

// Default type labels (overridden by CMS)
const DEFAULT_TYPE_LABELS = {
  retailer: 'Rivenditore',
  fabricator: 'Fabbricante',
  advisor: 'Advisor'
};

const TYPE_COLORS = {
  retailer: 'bg-amber-100 text-amber-700',
  fabricator: 'bg-stone-200 text-stone-700',
  advisor: 'bg-stone-200 text-stone-700'
};

/**
 * Professional Mini Card
 */
const ProfessionalMiniCard = ({ professional, materialId, onContact, cmsConfig }) => {
  const navigate = useNavigate();
  const type = professional.professional_type || 'retailer';
  
  // Get labels from CMS
  const typeLabels = {
    retailer: cmsConfig?.filter_retailer_label || DEFAULT_TYPE_LABELS.retailer,
    fabricator: cmsConfig?.filter_fabricator_label || DEFAULT_TYPE_LABELS.fabricator,
    advisor: cmsConfig?.filter_advisor_label || DEFAULT_TYPE_LABELS.advisor
  };
  
  // Track view event
  const trackView = async () => {
    try {
      await supabase.from('professional_contact_events').insert({
        professional_id: professional.id,
        material_id: materialId,
        event_type: 'view_profile',
        source_page: `/materials/${materialId}`
      });
    } catch (err) {
      console.error('Track error:', err);
    }
  };
  
  const handleClick = () => {
    trackView();
    navigate(`/retail-advisor?professional=${professional.slug}`);
  };
  
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4 hover:border-amber-300 transition-all">
      <div className="flex items-start gap-3">
        {/* Logo/Avatar */}
        <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {professional.logo_url ? (
            <img src={professional.logo_url} alt={professional.business_name} className="w-full h-full object-cover" />
          ) : (
            <Building2 className="w-6 h-6 text-stone-400" />
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <h4 className="text-stone-900 font-medium truncate">{professional.business_name}</h4>
            {professional.is_verified && (
              <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
            )}
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[type]}`}>
              {typeLabels[type] || type}
            </span>
            <span className="text-xs text-stone-500">{professional.city}</span>
          </div>
          
          {/* Rating */}
          {professional.rating > 0 && (
            <div className="flex items-center gap-1">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-3 h-3 ${i < Math.round(professional.rating) ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`}
                  />
                ))}
              </div>
              <span className="text-xs text-stone-500">({professional.review_count})</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex gap-2 mt-3">
        <Button
          variant="outline"
          size="sm"
          onClick={handleClick}
          className="flex-1 text-xs border-stone-200"
        >
          {cmsConfig?.details_button || 'Dettagli'}
        </Button>
        <Button
          size="sm"
          onClick={() => onContact(professional)}
          className="flex-1 text-xs bg-amber-600 hover:bg-amber-700 text-white"
        >
          {cmsConfig?.contact_button || 'Contatta'}
        </Button>
      </div>
    </div>
  );
};

/**
 * Contact/Photo Request Modal
 */
const RequestModal = ({ professional, materialName, materialId, user, onClose }) => {
  const [requestType, setRequestType] = useState('info');
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    message: '',
    items: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const photoItems = [
    { key: 'foto_ortogonale', label: 'Foto ortogonale della lastra' },
    { key: 'dettaglio_venature', label: 'Dettaglio venature e sfumature' },
    { key: 'finiture_disponibili', label: 'Finiture disponibili' },
    { key: 'formato_spessore', label: 'Formato e spessore disponibili' },
    { key: 'consiglio_applicativo', label: 'Consiglio applicativo' },
    { key: 'info_tecniche', label: 'Informazioni tecniche' }
  ];
  
  const toggleItem = (key) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.includes(key) 
        ? prev.items.filter(k => k !== key)
        : [...prev.items, key]
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Compila nome e email');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Insert request
      const request = {
        professional_id: professional.id,
        user_id: user?.id || null,
        material_id: materialId,
        material_name: materialName,
        request_type: requestType === 'photo' ? 'foto_lastra' : 'info',
        contact_name: formData.name,
        contact_email: formData.email,
        contact_phone: formData.phone,
        message: formData.message,
        requested_items: requestType === 'photo' ? formData.items : [],
        source_page: `/materials/${materialId}`,
        status: 'new'
      };
      
      const { error } = await supabase.from('material_requests').insert(request);
      if (error) throw error;
      
      // Track event
      await supabase.from('professional_contact_events').insert({
        professional_id: professional.id,
        material_id: materialId,
        event_type: requestType === 'photo' ? 'request_slab_photo' : 'request_info',
        source_page: `/materials/${materialId}`
      });
      
      toast.success('Richiesta inviata!');
      onClose();
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Errore nell\'invio');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div>
            <h3 className="text-stone-900 font-semibold">
              {requestType === 'photo' ? 'Richiedi foto della lastra' : 'Richiedi informazioni'}
            </h3>
            <p className="text-stone-500 text-sm">
              {materialName} • {professional.business_name}
            </p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Request Type Toggle */}
          <div className="flex gap-2 p-1 bg-stone-100 rounded-lg">
            <button
              type="button"
              onClick={() => setRequestType('info')}
              className={`flex-1 py-2 px-4 rounded text-sm font-medium transition-colors ${
                requestType === 'info' 
                  ? 'bg-white text-stone-900 shadow-sm' 
                  : 'text-stone-600'
              }`}
            >
              Informazioni
            </button>
            <button
              type="button"
              onClick={() => setRequestType('photo')}
              className={`flex-1 py-2 px-4 rounded text-sm font-medium transition-colors ${
                requestType === 'photo' 
                  ? 'bg-white text-stone-900 shadow-sm' 
                  : 'text-stone-600'
              }`}
            >
              Foto lastra
            </button>
          </div>
          
          {/* Photo request items */}
          {requestType === 'photo' && (
            <div className="space-y-2">
              <label className="text-sm text-stone-600">Cosa vorresti ricevere?</label>
              <div className="grid grid-cols-2 gap-2">
                {photoItems.map(item => (
                  <label
                    key={item.key}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.items.includes(item.key)
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.items.includes(item.key)}
                      onChange={() => toggleItem(item.key)}
                      className="hidden"
                    />
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      formData.items.includes(item.key)
                        ? 'bg-amber-500 border-amber-500'
                        : 'border-stone-300'
                    }`}>
                      {formData.items.includes(item.key) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-sm text-stone-700">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* Contact info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-stone-600 block mb-1.5">Nome *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-amber-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="text-sm text-stone-600 block mb-1.5">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-amber-500 focus:outline-none"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm text-stone-600 block mb-1.5">Telefono</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-amber-500 focus:outline-none"
              placeholder="+39 123 456 7890"
            />
          </div>
          
          <div>
            <label className="text-sm text-stone-600 block mb-1.5">Messaggio</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-amber-500 focus:outline-none resize-none"
              placeholder="Descrivi la tua richiesta..."
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Invia richiesta
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Main WhereToBuySection Component
 */
const WhereToBuySection = ({ materialId, materialName, user }) => {
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [cmsConfig, setCmsConfig] = useState(null);
  
  // Load CMS config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { data } = await supabase
          .from('blueprint_professionals_config')
          .select('*')
          .eq('lang', 'it')
          .single();
        
        if (data) {
          setCmsConfig(data);
        }
      } catch (err) {
        console.error('Error loading CMS config:', err);
      }
    };
    loadConfig();
  }, []);
  
  // Fetch professionals who have this material
  useEffect(() => {
    const fetchProfessionals = async () => {
      setIsLoading(true);
      try {
        // Get professional IDs that have this material
        const { data: materials } = await supabase
          .from('professional_materials')
          .select('professional_id')
          .eq('material_id', materialId)
          .eq('is_available', true);
        
        if (materials && materials.length > 0) {
          const profIds = materials.map(m => m.professional_id);
          
          // Get professional details
          const { data: profs } = await supabase
            .from('professionals')
            .select('*')
            .in('id', profIds)
            .eq('is_active', true)
            .eq('approval_status', 'approved')
            .order('rating', { ascending: false })
            .limit(4);
          
          setProfessionals(profs || []);
        } else {
          // No specific professionals, show featured ones
          const { data: featured } = await supabase
            .from('professionals')
            .select('*')
            .eq('is_active', true)
            .eq('approval_status', 'approved')
            .order('rating', { ascending: false })
            .limit(4);
          
          setProfessionals(featured || []);
        }
      } catch (err) {
        console.error('Error fetching professionals:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (materialId) {
      fetchProfessionals();
    }
  }, [materialId]);
  
  // Don't render if no professionals
  if (!isLoading && professionals.length === 0) {
    return null;
  }
  
  return (
    <section className="py-12 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-medium text-stone-900">
              {cmsConfig?.material_section_title || 'Dove trovare questo materiale'}
            </h2>
            <p className="text-stone-500 mt-1">
              {cmsConfig?.material_section_subtitle || 'Professionisti che dichiarano di avere questo materiale disponibile'}
            </p>
          </div>
          <button 
            onClick={() => navigate('/retail-advisor')}
            className="text-sm text-stone-600 hover:text-stone-900 flex items-center gap-1"
          >
            {cmsConfig?.view_all_label || 'Vedi tutti'} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {professionals.map(pro => (
              <ProfessionalMiniCard
                key={pro.id}
                professional={pro}
                materialId={materialId}
                cmsConfig={cmsConfig}
                onContact={setSelectedProfessional}
              />
            ))}
          </div>
        )}
        
        {/* CTA */}
        <div className="mt-8 p-6 bg-white rounded-xl border border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-medium text-stone-900">
              {cmsConfig?.photo_cta_title || 'Vuoi foto reali della lastra?'}
            </h3>
            <p className="text-sm text-stone-500">
              {cmsConfig?.photo_cta_subtitle || 'Contatta un professionista per ricevere foto HD delle lastre disponibili e usarle in SRE'}
            </p>
          </div>
          <Button 
            onClick={() => navigate('/retail-advisor')}
            className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
          >
            <MapPin className="w-4 h-4 mr-2" />
            {cmsConfig?.find_professionals_button || 'Trova professionisti'}
          </Button>
        </div>
      </div>
      
      {/* Contact Modal */}
      {selectedProfessional && (
        <RequestModal
          professional={selectedProfessional}
          materialName={materialName}
          materialId={materialId}
          user={user}
          cmsConfig={cmsConfig}
          onClose={() => setSelectedProfessional(null)}
        />
      )}
    </section>
  );
};

export default WhereToBuySection;
